/**
 * Поиск статей и объектов для TheSelect
 * @param {object} store
 * @param {string} search
 * @param {number} limit
 * @param {number} offset
 * @param {Array} val — выбранные значения (id или { id, type })
 */
export async function searchObjectsAndArticles(store, search, limit, offset, val) {
  const raw = Array.isArray(val) ? val : (val != null ? [val] : [])
  const resolveEntries = raw
    .map((entry) => {
      if (entry != null && typeof entry === 'object' && entry.vistat_id != null) {
        return {
          id: entry.vistat_id,
          type: normalizeVistatKind(entry.vistat_kind ?? entry.kind ?? entry.type)
        }
      }
      if (entry != null && typeof entry === 'object' && entry.id != null) {
        return { id: entry.id, type: normalizeVistatKind(entry.vistat_kind ?? entry.kind ?? entry.type) }
      }
      if (entry != null && (typeof entry === 'number' || typeof entry === 'string')) {
        return { id: entry, type: 'enc' }
      }
      return null
    })
    .filter(Boolean)

  const encIdsPrefetch = [...new Set(resolveEntries.filter((e) => e.type !== 'art').map((e) => e.id))]
  const artIdsPrefetch = [...new Set(resolveEntries.filter((e) => e.type === 'art').map((e) => e.id))]

  const q = search ? search.toString().trim() : ''
  const pageLimit = Number(limit) || 15
  const pageOffset = Number(offset) || 0
  const includePrefetch = pageOffset === 0

  const [objectsResp, articlesResp, prefObjResp, prefArtResp] = await Promise.allSettled([
    store.dispatch('objects/publicObjects', { q, limit: pageLimit, offset: pageOffset }),
    store.dispatch('art/publicObjects', { q, limit: pageLimit, offset: pageOffset }),
    includePrefetch && encIdsPrefetch.length
      ? store.dispatch('objects/publicObjects', {
        ids: encIdsPrefetch.map(String).join(','),
        limit: Math.max(encIdsPrefetch.length, 1),
      })
      : Promise.resolve({ items: [], total: 0 }),
    includePrefetch && artIdsPrefetch.length
      ? store.dispatch('art/publicObjects', {
        ids: artIdsPrefetch.map(String).join(','),
        limit: Math.max(artIdsPrefetch.length, 1),
      })
      : Promise.resolve({ items: [], total: 0 }),
  ])

  const objectsData = objectsResp.status === 'fulfilled' ? objectsResp.value : { items: [], total: 0 }
  const articlesData = articlesResp.status === 'fulfilled' ? articlesResp.value : { items: [], total: 0 }
  const prefObjectsData = prefObjResp.status === 'fulfilled' ? prefObjResp.value : { items: [], total: 0 }
  const prefArticlesData = prefArtResp.status === 'fulfilled' ? prefArtResp.value : { items: [], total: 0 }

  const objectItems = Array.isArray(objectsData?.items) ? objectsData.items : []
  const articleItems = Array.isArray(articlesData?.items) ? articlesData.items : []
  const prefObjectItems = Array.isArray(prefObjectsData?.items) ? prefObjectsData.items : []
  const prefArticleItems = Array.isArray(prefArticlesData?.items) ? prefArticlesData.items : []

  const rowKey = (row) => `${row.type}:${String(row.id)}`
  const selectedKeys = new Set(resolveEntries.map((entry) => `${entry.type}:${String(entry.id)}`))
  const byKey = new Map()

  const pushRows = (rows) => {
    for (const row of rows) {
      const k = rowKey(row)
      if (!byKey.has(k)) byKey.set(k, row)
    }
  }

  if (includePrefetch) {
    pushRows(
      prefObjectItems.map((o) => ({
        id: o.id,
        label: o.label || `${o.id}`,
        type: 'enc',
        typeLabel: 'Объект',
      }))
    )
    pushRows(
      prefArticleItems.map((a) => ({
        id: a.id ?? a.idx,
        label: a.label || a.title || `${a.id ?? a.idx}`,
        type: 'art',
        typeLabel: 'Статья',
      }))
    )
  }
  pushRows(
    objectItems.map((o) => ({
      id: o.id,
      label: o.label || `${o.id}`,
      type: 'enc',
      typeLabel: 'Объект',
    }))
  )
  pushRows(
    articleItems.map((a) => ({
      id: a.id ?? a.idx,
      label: a.label || a.title || `${a.id ?? a.idx}`,
      type: 'art',
      typeLabel: 'Статья',
    }))
  )

  const merged = [...byKey.values()]
  const filtered = sortRowsByQuery(
    merged.filter((row) => !selectedKeys.has(rowKey(row))),
    q
  )
  const filteredSelectedCount = merged.length - filtered.length
  const sourceTotal = Number(objectsData?.total || 0) + Number(articlesData?.total || 0)

  return {
    items: filtered,
    total: sourceTotal ? Math.max(sourceTotal - filteredSelectedCount, filtered.length) : filtered.length,
  }
}

function normalizeSearchText(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/ё/g, 'е')
    .trim()
}

function getSearchScore(row, query) {
  if (!query) return 0

  const label = normalizeSearchText(row?.label)
  const normalizedQuery = normalizeSearchText(query)

  if (!label || !normalizedQuery) return 0
  if (label === normalizedQuery) return 100
  if (label.startsWith(normalizedQuery)) return 80
  if (label.split(/\s+/).some((word) => word.startsWith(normalizedQuery))) return 60
  if (label.includes(normalizedQuery)) return 40

  return 0
}

function sortRowsByQuery(rows, query) {
  const normalizedQuery = normalizeSearchText(query)
  if (!normalizedQuery) return rows

  return rows
    .map((row, index) => ({
      row,
      index,
      score: getSearchScore(row, normalizedQuery),
    }))
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score
      return a.index - b.index
    })
    .map(({ row }) => row)
}

export function normalizeVistatKind(kind) {
  return kind === 'art' ? 'art' : 'enc'
}

export function linkedObjectToSelectOption(value) {
  if (!value) return null

  const id = value.vistat_id ?? value.id ?? value.value
  if (id == null) return null

  const type = normalizeVistatKind(value.vistat_kind ?? value.kind ?? value.type)

  return {
    ...value,
    id,
    label: value.label || value.title || String(id),
    type,
    typeLabel: value.typeLabel || (type === 'art' ? 'Статья' : 'Объект'),
  }
}

export function linkedObjectToPayload(value) {
  const option = linkedObjectToSelectOption(value)
  if (!option) return null

  return {
    vistat_kind: option.type,
    vistat_id: String(option.id),
    label: option.label,
  }
}

/** Загрузка карточек top_items категории в порядке ранга */
export async function fetchCategoryTopItems(store, topItems = []) {
  const raw = Array.isArray(topItems) ? topItems : []
  if (!raw.length) {
    return []
  }

  const encIds = []
  const artIds = []
  const orderKeys = []

  for (const item of raw) {
    const id = item.vistat_id ?? item.id
    if (id == null) continue
    const kind = item.kind === 'art' ? 'art' : 'enc'
    orderKeys.push(`${kind}:${String(id)}`)
    if (kind === 'art') {
      artIds.push(String(id))
    } else {
      encIds.push(String(id))
    }
  }

  if (!orderKeys.length) {
    return []
  }

  const encUnique = [...new Set(encIds)]
  const artUnique = [...new Set(artIds)]

  const [objectsData, articlesData] = await Promise.all([
    encUnique.length
      ? store.dispatch('objects/publicObjects', {
        ids: encUnique.join(','),
        limit: encUnique.length,
        is_about_project: null,
      }).catch(() => ({ items: [] }))
      : Promise.resolve({ items: [] }),
    artUnique.length
      ? store.dispatch('art/publicObjects', {
        ids: artUnique.join(','),
        limit: artUnique.length,
        is_about_project: null,
      }).catch(() => ({ items: [] }))
      : Promise.resolve({ items: [] }),
  ])

  const byKey = new Map()
  for (const objectItem of objectsData.items || []) {
    byKey.set(`enc:${objectItem.id}`, { ...objectItem, _topKind: 'enc' })
  }
  for (const articleItem of articlesData.items || []) {
    const id = articleItem.id ?? articleItem.idx
    byKey.set(`art:${id}`, { ...articleItem, _topKind: 'art' })
  }

  return orderKeys.map((key) => byKey.get(key)).filter(Boolean)
}

export function topItemAssetFromApi(item = {}) {
  const id = item.vistat_id ?? item.id
  if (id == null) return null
  const type = item.kind === 'art' ? 'art' : 'enc'
  return {
    id,
    label: String(id),
    type,
    typeLabel: type === 'art' ? 'Статья' : 'Объект',
  }
}

/** Строки формы → payload top_items (порядок = ранг), см. ObjectCategoryTopItemForm */
export function topItemsToPayload(rows = []) {
  const seen = new Set()
  return (rows || [])
    .map((row) => row?.asset)
    .filter((asset) => asset?.id != null)
    .map((asset) => ({
      vistat_id: String(asset.id),
      kind: asset.type === 'art' ? 'art' : 'enc',
    }))
    .filter((item) => {
      const k = `${item.kind}:${String(item.vistat_id)}`
      if (seen.has(k)) return false
      seen.add(k)
      return true
    })
}
